import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { getFirebaseBackend } from 'src/app/authUtils';
import { environment } from 'src/environments/environment';
import { User } from '../models/auth.models';
import { CommonService } from './common.service';

export interface JwtClaims {
  exp?: number;
  phase?: string | number;
  role?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'authToken';
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private http: HttpClient, private commonService: CommonService, private zone: NgZone, private router: Router) {}

  login(emailid: string, loginpassword: string): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}Users/VerifyCredential?storecode=&emailid=${encodeURIComponent(emailid)}&password=${encodeURIComponent(loginpassword)}`)
      .pipe(catchError(this.handleError));
  }

  verifyLogin(data: User): Observable<any> {
    const headers = new HttpHeaders().set('content-type', 'application/json');
    return this.http.post<any>(`${environment.baseUrl}Users/VerifyLoginCredential`, JSON.stringify(data.toMap()), { headers })
      .pipe(catchError(this.handleError));
  }

  /** The backend owns the special credentials; the frontend never grants admin itself. */
  verifySuperAdmin(): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}Users/VerifyCredential?emailid=superadmin&password=super`)
      .pipe(catchError(this.handleError));
  }

  /** Saves only a valid, non-expired JWT and starts its automatic logout timer. */
  storeToken(token: string): boolean {
    if (!token || !this.decodeToken(token) || this.isTokenExpired(token)) {
      this.clearSession();
      return false;
    }
    sessionStorage.setItem(this.tokenKey, token);
    this.watchTokenExpiry(token);
    return true;
  }

  /** Reinstates the expiry timer after a browser refresh. */
  restoreSession(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const restored = this.storeToken(token);
    if (!restored) this.router.navigate(['/auth/login']);
    return restored;
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  decodeToken(token: string | null = this.getToken()): JwtClaims | null {
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(Array.prototype.map.call(atob(normalized), (c: string) =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(json) as JwtClaims;
    } catch {
      return null;
    }
  }

  getRole(): string | null {
    const claims = this.decodeToken();
    return this.claim(claims, ['role', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
  }

  getPhase(): number | null {
    const phase = this.claim(this.decodeToken(), ['phase', 'Phase']);
    const value = Number(phase);
    return Number.isFinite(value) ? value : null;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token || !this.decodeToken(token) || this.isTokenExpired(token)) {
      this.clearSession();
      return false;
    }
    return true;
  }

  /** Admin has every permission; normal users inherit every phase below their own. */
  canAccessPhase(requiredPhase: number): boolean {
    if (!this.isLoggedIn()) return false;
    if (this.getRole()?.toLowerCase() === 'admin') return true;
    const phase = this.getPhase();
    return phase !== null && phase >= requiredPhase;
  }

  logout(): void {
    this.clearSession();
    // Best-effort notification only; logout must never depend on a network response.
    this.http.post(`${environment.baseUrl}Users/LogOutUser`, null).pipe(catchError(() => throwError(null))).subscribe({ error: () => undefined });
  }

  logoutbyTimeout(): void { this.clearSession(); }

  currentUser(): any[] | null {
    const raw = sessionStorage.getItem(environment.dataKey);
    if (!raw) return null;
    try { return JSON.parse(this.commonService.atou(raw)); } catch { return null; }
  }

  getStoreConfig(storecode: string): Observable<any> {
    return this.http.get<any>(`${environment.baseUrl}Storesetup/GetMofoterProConfig?storecode=${encodeURIComponent(storecode)}`)
      .pipe(catchError(this.handleError));
  }

  register(email: string, password: string) { return getFirebaseBackend().registerUser(email, password); }
  resetPassword(email: string) { return getFirebaseBackend().forgetPassword(email); }

  private claim(claims: JwtClaims | null, names: string[]): string | null {
    if (!claims) return null;
    for (const name of names) {
      const value = claims[name];
      if (value !== undefined && value !== null) return String(value);
    }
    return null;
  }

  private isTokenExpired(token: string): boolean {
    const exp = this.decodeToken(token)?.exp;
    return !exp || Number(exp) * 1000 <= Date.now();
  }

  private watchTokenExpiry(token: string): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    const expiresAt = Number(this.decodeToken(token)?.exp) * 1000;
    const delay = expiresAt - Date.now();
    this.expiryTimer = setTimeout(() => this.zone.run(() => {
      this.clearSession();
      this.router.navigate(['/auth/login']);
    }), Math.max(delay, 0));
  }

  private clearSession(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    sessionStorage.clear();
    localStorage.clear();
  }

  private handleError(error: HttpErrorResponse) {
    return throwError('Something bad happened; please try again later.');
  }
}

// Existing feature modules use this historic name; retain it while exposing AuthService.
export { AuthService as AuthenticationService };
